import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosInstance';
import { Button } from '@/components/ui/button';
import { PageLoader } from '@/components/ui/page-loader';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Label } from '@/components/ui/label';
import { Dropdown } from '@/components/ui/dropdown';
import { BASE_URL } from '@/config/constants';
import { toast } from 'sonner';
import { Video } from 'lucide-react';

import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Input } from '@/components/ui/input';

interface Video {
  _id: string;
  title: string;
  category: string;
  youtube: string;
  thumbnail: string;
  order: number;
}

interface Category {
  _id: string;
  name: string;
  parentCategory?: {
    _id: string;
    name: string;
  } | null;
}

interface SortableTableRowProps {
  item: Video;
  index: number;
  categories: Category[];
  onEdit: (item: Video) => void;
  onDelete: (id: string) => void;
}

const SortableTableRow: React.FC<SortableTableRowProps> = ({ item, index, categories, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item._id });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    backgroundColor: isDragging ? 'rgba(0, 0, 0, 0.05)' : undefined,
  };

  return (
    <TableRow ref={setNodeRef} style={style} className="hover:bg-muted/50 transition-colors">
      <TableCell className="w-10 select-none cursor-grab">
        <div className="flex items-center gap-2" {...attributes} {...listeners}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground">
            <circle cx="9" cy="12" r="1"/><circle cx="9" cy="5" r="1"/><circle cx="9" cy="19" r="1"/>
            <circle cx="15" cy="12" r="1"/><circle cx="15" cy="5" r="1"/><circle cx="15" cy="19" r="1"/>
          </svg>
          <span className="w-4 text-muted-foreground font-medium">{index + 1}</span>
        </div>
      </TableCell>
      <TableCell className="font-medium text-foreground max-w-[200px]">
        <div className="break-words whitespace-normal">
          {item.title}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          {(() => {
            const cat = categories.find(c => c._id === item.category);
            return (
              <>
                {cat?.parentCategory && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-info-light text-info">
                    {cat.parentCategory.name}
                  </span>
                )}
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                  cat?.parentCategory 
                    ? 'bg-success-light text-primary' 
                    : 'bg-secondary text-foreground'
                }`}>
                  {cat?.name || '-'}
                </span>
              </>
            );
          })()}
        </div>
      </TableCell>
      <TableCell>
        <a href={item.youtube} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
          YouTube
        </a>
      </TableCell>
      <TableCell>
        {item.thumbnail ? (
          <img
            src={item.thumbnail}
            alt="thumbnail"
            className="w-16 h-16 object-cover rounded"
          />
        ) : (
          <span className="text-sm text-muted-foreground">-</span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => onEdit(item)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </Button>
          <Button
            variant="destructive"
            size="icon-sm"
            onClick={() => onDelete(item._id)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

const emptyVideo = {
  title: '',
  category: '',
  youtube: '',
  thumbnail: '',
  order: 0,
};

const extractYouTubeVideoId = (url: string): string | null => {
  const regex = /(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?|watch)(?:\.php)?(?:\?|\#\!)?v=|(?:watch)?\?.*?&?v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

interface VideoManagerProps {
  domain: string;
}

const VideoManager: React.FC<VideoManagerProps> = ({ domain }) => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof emptyVideo>(emptyVideo);
  const [editId, setEditId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedParent, setSelectedParent] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = videos.findIndex((item) => item._id === active.id);
      const newIndex = videos.findIndex((item) => item._id === over.id);
      
      const newItems = arrayMove(videos, oldIndex, newIndex);
      setVideos(newItems);
      
      try {
        await axios.post(`${BASE_URL}/api/videos/reorder`, { items: newItems });
        toast.success('Video order updated successfully!');
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Failed to update video order');
        // Revert the order if the API call fails
        fetchVideos();
      }
    }
  };

  const fetchVideos = () => {
    setLoading(true);
    axios
      .get(BASE_URL + '/api/videos')
      .then(res => setVideos(res.data))
      .finally(() => setLoading(false));
  };

  const fetchCategories = () => {
    axios.get(BASE_URL + '/api/category', { params: { type: 'video' } })
      .then(res => setCategories(res.data));
  };

  useEffect(() => {
    fetchVideos();
    fetchCategories();
  }, [domain]);

  const fetchYouTubeVideoDetails = async (videoId: string) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/youtube/video-details/${videoId}`);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch video details:', err);
      return null;
    }
  };

  const parentCategories = categories.filter(cat => !cat.parentCategory);
  const subcategories = categories.filter(cat => cat.parentCategory && cat.parentCategory._id === selectedParent);
  const hasSubcategories = subcategories.length > 0;

  const renderCategoryInputs = () => (
    <div className="space-y-2">
      <Dropdown
        value={selectedParent}
        options={[
          { value: '', label: 'Select Category' },
          ...parentCategories.map(p => ({ value: p._id, label: p.name })),
        ]}
        onValueChange={(val) => {
          const parentId = val as string;
          setSelectedParent(parentId);
          if (!parentId) {
            setForm(prev => ({ ...prev, category: '' }));
          } else {
            const hasSub = categories.some(cat => cat.parentCategory?._id === parentId);
            setForm(prev => ({ ...prev, category: hasSub ? '' : parentId }));
          }
        }}
      />

      {hasSubcategories && (
        <Dropdown
          value={form.category}
          options={[
            { value: '', label: 'Select Subcategory' },
            ...subcategories.map(s => ({ value: s._id, label: s.name })),
          ]}
          onValueChange={(val) => setForm(prev => ({ ...prev, category: val as string }))}
        />
      )}

      {!hasSubcategories && selectedParent && (
        <input type="hidden" name="category" value={selectedParent} />
      )}
    </div>
  );

  const handleFormChange = async (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const target = e.target as HTMLInputElement;
    const { name, value } = target;

    const updatedForm = {
      ...form,
      [name]: value,
    };

    if (name === 'youtube') {
      const videoId = extractYouTubeVideoId(value);
      if (videoId) {
        updatedForm.thumbnail = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
        
        // Fetch video details
        const videoDetails = await fetchYouTubeVideoDetails(videoId);
        if (videoDetails?.title) {
          updatedForm.title = videoDetails.title;
        }
      }
    }

    setForm(updatedForm);
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const response = await axios.post(BASE_URL + '/api/videos', form);
      setAddOpen(false);
      setForm(emptyVideo);
      toast.success('Video added successfully!');
   
      if (response.data) {
        setVideos(prev => [...prev, response.data]);
      } else {
        fetchVideos();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to add video';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item: Video) => {
    setEditId(item._id);
    const cat = categories.find(c => c._id === item.category);

    // Determine if it's a subcategory or top-level category
    if (cat?.parentCategory?._id) {
      setSelectedParent(cat.parentCategory._id);
    } else if (cat?._id) {
      setSelectedParent(cat._id);
    } else {
      setSelectedParent('');
    }

    setForm(item);
    setEditOpen(true);
    setError('');
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    const currentEditId = editId;
    try {
      const response = await axios.put(`${BASE_URL}/api/videos/${editId}`, form);
      setEditOpen(false);
      setEditId(null);
      setForm(emptyVideo);
      toast.success('Video updated successfully!');

      if (response.data) {
        setVideos(prev => prev.map(video =>
          video._id === currentEditId ? { ...video, ...response.data } : video
        ));
      } else {
        fetchVideos();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to update video';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const deletedVideo = videos.find(video => video._id === deleteId);
    // Optimistic removal
    setVideos(prev => prev.filter(video => video._id !== deleteId));
    setDeleteId(null);
    try {
      await axios.delete(`${BASE_URL}/api/videos/${deleteId}`);
      toast.success('Video deleted successfully!');

    } catch (err: any) {
      // Revert on error
      if (deletedVideo) {
        setVideos(prev => [...prev, deletedVideo].sort((a, b) => a.order - b.order));
      }
      const errorMessage = err.response?.data?.message || 'Failed to delete video';
      toast.error(errorMessage);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <h2 className="page-title">Video Management</h2>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button>Add Video</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Video</DialogTitle>
              <DialogDescription>Add a new video with YouTube URL, title, category and thumbnail</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAdd} className="space-y-5">
              <div className="form-group">
                <Label htmlFor="youtube" className="form-label">YouTube URL</Label>
                <Input
                  id="youtube"
                  name="youtube"
                  placeholder="Enter YouTube video URL"
                  value={form.youtube}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <Label htmlFor="title" className="form-label">Title</Label>
                <Input
                  id="title"
                  name="title"
                  placeholder="Enter video title"
                  value={form.title}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <Label htmlFor="thumbnail" className="form-label">Thumbnail URL</Label>
                <Input
                  id="thumbnail"
                  name="thumbnail"
                  placeholder="Enter thumbnail URL"
                  value={form.thumbnail}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <Label className="form-label">Category</Label>
                {renderCategoryInputs()}
              </div>
              {error && <div className="text-danger">{error}</div>}
              <Button type="submit" loading={saving} loadingText="Adding...">Add</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div>
        {loading ? (
          <PageLoader label="Loading videos..." />
        ) : videos.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <Video className="w-full h-full" />
            </div>
            <h3 className="empty-state-title">No videos found</h3>
            <p className="empty-state-description">Add your first video to get started</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-10">Order</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>YouTube</TableHead>
                  <TableHead>Thumbnail</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="text-left">
                <SortableContext items={videos.map(item => item._id)} strategy={verticalListSortingStrategy}>
                  {videos.map((item, idx) => (
                    <SortableTableRow
                      key={item._id}
                      item={item}
                      index={idx}
                      categories={categories}
                      onEdit={handleEdit}
                      onDelete={setDeleteId}
                    />
                  ))}
                </SortableContext>
              </TableBody>
            </Table>
          </DndContext>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Video</DialogTitle>
            <DialogDescription>Update the video details including YouTube URL, title, category and thumbnail</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-5">
              <div className="form-group">
                <Label htmlFor="edit-youtube" className="form-label">YouTube URL</Label>
                <Input
                  id="edit-youtube"
                  name="youtube"
                  placeholder="Enter YouTube video URL"
                  value={form.youtube}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <Label htmlFor="edit-title" className="form-label">Title</Label>
                <Input
                  id="edit-title"
                  name="title"
                  placeholder="Enter video title"
                  value={form.title}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <Label htmlFor="edit-thumbnail" className="form-label">Thumbnail URL</Label>
                <Input
                  id="edit-thumbnail"
                  name="thumbnail"
                  placeholder="Enter thumbnail URL"
                  value={form.thumbnail}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <Label className="form-label">Category</Label>
                {renderCategoryInputs()}
              </div>
            {error && <div className="text-danger">{error}</div>}
            <Button type="submit" loading={saving} loadingText="Saving...">Save</Button>
          </form>
        </DialogContent>
      </Dialog>


      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => !open && setDeleteId(null)}
        title="Delete Video"
        description="Are you sure you want to delete this video? This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default VideoManager;
